import React, { useState } from 'react';
import styled from 'styled-components';
import * as Icon from 'react-feather';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import Table from 'components/widgets/table';

const Container = styled.div`
  width: 100%;
  padding: 24px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.06);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
`;

const Title = styled.h2`
  margin: 0;
  color: #222;
`;

const Section = styled.section`
  margin-bottom: 18px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  align-items: start;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  ${(p) => (p.full ? 'grid-column: 1 / -1;' : '')}
`;

const Label = styled.label`
  font-size: 13px;
  color: #444;
  margin-bottom: 6px;
  font-weight: 600;
`;

const sharedInputStyles = `
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  font-size: 14px;
  color: #222;
  box-sizing: border-box;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37,99,235,0.08);
  }
`;

const Input = styled.input`
  ${sharedInputStyles}
`;

const Textarea = styled.textarea`
  ${sharedInputStyles}
  min-height: 92px;
  resize: vertical;
`;

const Select = styled.select`
  ${sharedInputStyles}
  appearance: none;
  background-image: linear-gradient(45deg, transparent 50%, #6b7280 50%), linear-gradient(135deg, #6b7280 50%, transparent 50%);
  background-position: calc(100% - 18px) calc(1em + 2px), calc(100% - 13px) calc(1em + 2px);
  background-size: 7px 7px, 7px 7px;
  background-repeat: no-repeat;
  padding-right: 36px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ButtonPrimary = styled.button`
  background: #001f5f;
  color: #fff;
  border: none;
  padding: 8px 14px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const ButtonGhost = styled.button`
  background: transparent;
  color: #001f5f;
  border: 1px solid #e6eefc;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 160px 160px 160px 160px;
  gap: 12px;
  padding: 12px 16px;
  align-items: center;
  border-top: 1px solid #f6f6f6;
`;

const HeadRow = styled(TableRow)`
  background: #f8f9fa;
  font-weight: 700;
`;

const Small = styled.div`
  font-size: 13px;
  color: #666;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
  grid-column: 2 / 3;

  @media (max-width: 800px) {
    grid-column: 1 / -1;
    justify-content: flex-start;
  }
`;

export default function ProductDocumentConfig() {
  const [docs, setDocs] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [formValues, setFormValues] = useState({
    name: '',
    issuance: 'manual',
    description: '',
    template: '',
    type: 'Cover summary',
    integrator: '',
    policyEvent: 'new',
  });

  const templateOptions = [
    { value: '', label: 'Select template' },
    { value: 'standard', label: 'Standard Template' },
    { value: 'detailed', label: 'Detailed Template' },
  ];

  const integratorOptions = [
    { value: '', label: 'Select integrator' },
    { value: 'int-1', label: 'Integrator One' },
    { value: 'int-2', label: 'Integrator Two' },
  ];

  function resetForm() {
    setFormValues({
      name: '',
      issuance: 'manual',
      description: '',
      template: '',
      type: 'Cover summary',
      integrator: '',
      policyEvent: 'new',
    });
    setEditingId(null);
  }

  function saveDoc(e) {
    e?.preventDefault();
  }

  function editDoc(id) {
    const doc = docs.find((d) => d.id === id);
    if (!doc) return;
    setFormValues({
      name: doc.name || '',
      issuance: doc.issuance || 'manual',
      description: doc.description || '',
      template: doc.template || '',
      type: doc.type || 'Cover summary',
      integrator: doc.integrator || '',
      policyEvent: doc.policyEvent || 'new',
    });
    setEditingId(id);
  }

  function removeDoc(id) {
    if (!window.confirm('Remove this document configuration?')) return;
    setDocs((p) => p.filter((d) => d.id !== id));
    if (editingId === id) resetForm();
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Document name is required'),
    issuance: Yup.string().required('Issuance is required'),
    type: Yup.string().required('Type is required'),
    policyEvent: Yup.string().required('Policy event is required'),
    integrator: Yup.string().when('issuance', {
      is: (val) => val === 'integrator',
      then: (schema) => schema.required('Please select an integrator'),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const table_fields = [
    { name: 'Name' },
    { name: 'Issuance' },
    { name: 'Type' },
    { name: 'Policy event' },
    { name: 'Actions' },
  ];

  const row_data = [
    { type: 'text', field: 'name' },
    { type: 'text', field: 'issuance' },
    { type: 'text', field: 'type' },
    { type: 'text', field: 'policyEvent' },
    {
      type: 'action',
      actions: [
        { method: (id) => editDoc(id), field: 'id', title: 'Edit' },
        { method: (id) => removeDoc(id), field: 'id', title: 'Remove' },
      ],
    },
  ];

  return (
    <Container>
      <Header>
        <Title>Product Document Configuration</Title>
      </Header>

      <Section>
        <Small style={{ marginBottom: 8, fontWeight: 700 }}>Add / edit document</Small>

        <Formik
          enableReinitialize
          initialValues={formValues}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            const payload = {
              id: editingId || Date.now().toString(),
              ...values,
            };

            if (editingId) {
              setDocs((prev) => prev.map((d) => (d.id === editingId ? payload : d)));
            } else {
              setDocs((prev) => [payload, ...prev]);
            }

            setSubmitting(false);
            resetForm();
            resetForm();
            setFormValues({
              name: '',
              issuance: 'manual',
              description: '',
              template: '',
              type: 'Cover summary',
              integrator: '',
              policyEvent: 'new',
            });
            setEditingId(null);
          }}
        >
          {({ values, handleChange, setFieldValue, isSubmitting }) => (
            <Form>
              <Row>
                <FormGroup>
                  <Label>Document name</Label>
                  <Field name="name">{({ field }) => <Input {...field} placeholder="e.g. Policy Schedule" />}</Field>
                  <ErrorMessage name="name" component="div" className="invalid-feedback d-block" />
                </FormGroup>

                <FormGroup>
                  <Label>Issuance</Label>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <input type="radio" name="issuance" value="manual" checked={values.issuance === 'manual'} onChange={() => setFieldValue('issuance', 'manual')} />
                      <Small>Manual</Small>
                    </label>

                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <input type="radio" name="issuance" value="underwriter" checked={values.issuance === 'underwriter'} onChange={() => setFieldValue('issuance', 'underwriter')} />
                      <Small>Underwriter</Small>
                    </label>

                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <input type="radio" name="issuance" value="integrator" checked={values.issuance === 'integrator'} onChange={() => setFieldValue('issuance', 'integrator')} />
                      <Small>Integrator</Small>
                    </label>
                  </div>
                  <ErrorMessage name="issuance" component="div" className="invalid-feedback d-block" />
                </FormGroup>

                <FormGroup>
                  <Label>Type</Label>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <input type="radio" name="type" value="Cover summary" checked={values.type === 'Cover summary'} onChange={() => setFieldValue('type', 'Cover summary')} />
                      <Small>Cover summary</Small>
                    </label>

                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <input type="radio" name="type" value="Motor certificate" checked={values.type === 'Motor certificate'} onChange={() => setFieldValue('type', 'Motor certificate')} />
                      <Small>Motor certificate</Small>
                    </label>
                  </div>
                  <ErrorMessage name="type" component="div" className="invalid-feedback d-block" />
                </FormGroup>

                <FormGroup>
                  <Label>Template</Label>
                  <Field as={Select} name="template" value={values.template} onChange={handleChange}>
                    {templateOptions.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </Field>
                </FormGroup>

                <FormGroup>
                  <Label>Integrator</Label>
                  <Field as={Select} name="integrator" disabled={values.issuance !== 'integrator'} value={values.integrator} onChange={handleChange}>
                    {integratorOptions.map((i) => (
                      <option key={i.value} value={i.value}>{i.label}</option>
                    ))}
                  </Field>
                  <ErrorMessage name="integrator" component="div" className="invalid-feedback d-block" />
                </FormGroup>

                <FormGroup full>
                  <Label>Description</Label>
                  <Field name="description">{({ field }) => <Textarea {...field} placeholder="Optional description" />}</Field>
                </FormGroup>

                <FormGroup>
                  <Label>Policy event</Label>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <input type="radio" name="policyEvent" value="new" checked={values.policyEvent === 'new'} onChange={() => setFieldValue('policyEvent', 'new')} />
                      <Small>New policy</Small>
                    </label>

                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <input type="radio" name="policyEvent" value="renewal" checked={values.policyEvent === 'renewal'} onChange={() => setFieldValue('policyEvent', 'renewal')} />
                      <Small>Renewal</Small>
                    </label>
                  </div>
                  <ErrorMessage name="policyEvent" component="div" className="invalid-feedback d-block" />
                </FormGroup>

                <Actions>
                  <ButtonPrimary type="submit" disabled={isSubmitting}>
                    <Icon.Plus size={14} /> {editingId ? 'Save changes' : 'Add'}
                  </ButtonPrimary>
                  {editingId && (
                    <ButtonGhost
                      type="button"
                      onClick={() => {
                        resetForm();
                        setFormValues({ name: '', issuance: 'manual', description: '', template: '', type: 'Cover summary', integrator: '', policyEvent: 'new' });
                      }}
                    >
                      Cancel
                    </ButtonGhost>
                  )}
                </Actions>
              </Row>
            </Form>
          )}
        </Formik>
       </Section>

       <Section>
         <Small style={{ marginBottom: 8, fontWeight: 700 }}>Configured product documents</Small>
         <Table
           title="Product Document Configurations"
           table_fields={table_fields}
           table_data={docs}
           row_data={row_data}
           pageSize={10}
         />
       </Section>
     </Container>
   );
 }
